import React, { useState, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Box, TextField, Tabs, Tab,
  CircularProgress, Alert, Paper, IconButton
} from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { extractRecipeWithGemini } from "../utils/geminiRecipeService";

const AiRecipeAssistantDialog = ({
  open,
  onClose,
  onRecipeExtracted
}) => {
  const [tabIndex, setTabIndex] = useState(0); // 0: Photo / Card Scan, 1: Text Prompt
  const [promptText, setPromptText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Image size must be less than 10MB.");
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExtract = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const extracted = await extractRecipeWithGemini({
        promptText: promptText.trim(),
        imageFile: selectedFile
      });

      onRecipeExtracted(extracted);
      onClose();
      // Reset state
      setPromptText("");
      handleClearFile();
    } catch (err) {
      console.error("AI Recipe Extraction failed:", err);
      setError(err.message || "Failed to extract recipe. Please try again with clearer text or photo.");
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = !isLoading && (selectedFile || promptText.trim().length > 5);

  return (
    <Dialog open={open} onClose={isLoading ? null : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#D28415' }}>
        <AutoAwesomeIcon />
        <Typography variant="h6" component="span" sx={{ fontFamily: "'Patrick Hand', cursive", fontSize: '1.4rem' }}>
          AI Recipe Assistant (Gemini)
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => { setTabIndex(val); setError(null); }}
          variant="fullWidth"
          sx={{ mb: 2.5 }}
        >
          <Tab icon={<CameraAltIcon />} label="Scan Card / Photo" iconPosition="start" />
          <Tab icon={<TextFieldsIcon />} label="Text & Rough Notes" iconPosition="start" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {tabIndex === 0 ? (
          /* TAB 0: Image / Recipe Card OCR */
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload or snap a photo of a handwritten index card, printed family recipe, or magazine clipping.
            </Typography>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {!previewUrl ? (
              <Paper
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: '#D28415',
                  bgcolor: '#FAF7F2',
                  '&:hover': { bgcolor: '#F5EFE6' },
                  borderRadius: 3
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: '#D28415', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Tap to Choose Image or Take Photo
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supports JPG, PNG, WEBP (up to 10MB)
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ position: 'relative', textAlign: 'center', my: 1 }}>
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Recipe Card Preview"
                  sx={{
                    maxHeight: 280,
                    maxWidth: '100%',
                    borderRadius: 2,
                    boxShadow: 2,
                    objectFit: 'contain'
                  }}
                />
                <IconButton
                  onClick={handleClearFile}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <TextField
              fullWidth
              size="small"
              placeholder="Optional: Add extra notes or corrections for the AI..."
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        ) : (
          /* TAB 1: Text Prompt / Messy Notes */
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Paste rough recipe text, ingredients copied from a text message, or type a casual description:
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="e.g. Grandma's Swedish Meatballs: 1 lb ground beef, 1 lb pork, 1/2 cup breadcrumbs, 1/4 cup heavy cream, 1 egg, 1/2 tsp allspice. Mix and form 1-inch balls. Brown in butter, simmer in beef broth for 15 mins. Serves 6."
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExtract}
          disabled={!canSubmit}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
          sx={{
            bgcolor: '#D28415',
            '&:hover': { bgcolor: '#b36b10' },
            fontWeight: 'bold',
            minWidth: 140
          }}
        >
          {isLoading ? "Analyzing..." : "Auto-Fill Recipe"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiRecipeAssistantDialog;
